import { COLORS } from "@/theme/localTheme"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import { DatePickerProps, LocalizationProvider, DatePicker as MUIDatePicker } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import { useContext } from "react"
import { Controller, get, useFormContext } from "react-hook-form"
import { CustomSX, IFieldContext } from "../../../../../interface/FormGenerator"
import FieldContext from "../../FieldContext"

const utc = require("dayjs/plugin/utc")
var customParseFormat = require("dayjs/plugin/customParseFormat")
dayjs.extend(utc)
dayjs.extend(customParseFormat)

const DatePickerSX: CustomSX = {
  enable: {
    width: "100%",
    "& .MuiOutlinedInput-input": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& fieldset": {
      borderColor: "#3CA63C",
    },
    "&:hover fieldset": {
      borderColor: "#3CA63C",
    },
    "& .MuiInputBase-root.Mui-focused fieldset": {
      "&:hover fieldset": {
        borderColor: "#3CA63C",
      },
      borderColor: "#3CA63C",
    },
    "& .MuiInputBase-root:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline ": {
      borderColor: "#3CA63C",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& .MuiSvgIcon-root": {
      color: "white", // Set the color for the calendar icon
    },
  },
  disable: {
    width: "100%",
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "white",
    },

    "& .MuiOutlinedInput-input": {
      color: COLORS?.adminAppTextPrimary, // Grey color for text
    },
    "& fieldset": {
      borderColor: COLORS?.adminAppTextPrimary, // Grey color for border
    },
    "&:hover fieldset": {
      borderColor: COLORS?.adminAppTextPrimary, // Grey color for border on hover
    },
    "& .MuiInputBase-root.Mui-focused fieldset": {
      "&:hover fieldset": {
        borderColor: COLORS?.adminAppTextPrimary, // Grey color for border on focus
      },
      borderColor: COLORS?.adminAppTextPrimary, // Grey color for border on focus
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS?.adminAppTextPrimary, // Grey color for border on hover
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: COLORS?.adminAppTextPrimary, // Grey color for label on focus
    },

    "& .MuiInputBase-root.Mui-disabled": {
      "& .MuiOutlinedInput-input": {
        color: "rgba(255, 255, 255, 0.7) !important", // Change this line to set the text color to white for disabled state
        WebkitTextFillColor: "rgba(255, 255, 255, 0.7)",
      },
      "& fieldset": {
        borderColor: COLORS?.adminAppTextPrimary, // Grey color for border when disabled
      },
      "& .MuiSvgIcon-root": {
        color: COLORS?.adminAppTextPrimary, // Set the color for the calendar icon
      },
    },
  },
}

const DatePicker = (props?: Partial<IFieldContext & DatePickerProps<Dayjs>>) => {
  const { control, register, setValue, getValues, formState } = useFormContext()
  const { errors } = formState
  const contextData = useContext(FieldContext)

  const { formKey, form, onChange: onValueChange, ...rest } = Object.keys(contextData).length > 0 ? contextData : (props as IFieldContext)
  const datePickerProps: DatePickerProps<Dayjs> = { ...rest }

  if (!form) throw new Error("Missing Parameter: Field Context")

  if (!formKey) throw new Error("Missing Parameter: Form Key")

  const error = get(errors, formKey)

  const { answer_value } = form

  const format = form?.format ?? "DD-MM-YYYY"
  const isValid = dayjs(answer_value as string | number, format, true).isValid()

  const defaultValue =
    typeof answer_value === "string" && answer_value.length && isValid
      ? dayjs(answer_value, format).unix() * 1000 //
      : typeof answer_value === "number" && answer_value !== 0
        ? dayjs(answer_value).unix() * 1000
        : null

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          key={form?.id}
          name={formKey}
          control={control}
          defaultValue={defaultValue}
          rules={{
            required: typeof form.required === "boolean" && form.required ? "Required" : form.required,
            validate: (value: Dayjs | string | number) => {
              let parsedValue = value
              if (typeof value === "string" || typeof value === "number") parsedValue = dayjs(value)
              if (datePickerProps.minDate && (parsedValue as Dayjs).isBefore(datePickerProps.minDate)) return "Passed minimum date"
              else return undefined
            },
          }}
          render={({ field: { ref, ...fields } }) => {
            return (
              <MUIDatePicker
                {...fields}
                disabled={!form.can_edit}
                format={format}
                disablePast={form.disable_past}
                label={form?.label}
                value={dayjs(fields.value)}
                minDate={form?.min_date ? dayjs(form.min_date) : undefined}
                maxDate={form?.max_date ? dayjs(form.max_date) : undefined}
                onChange={(date) => {
                  const formattedDate = date ? dayjs(date).unix() * 1000 : null
                  fields.onChange(formattedDate)
                }}
                sx={{ ...(form.can_edit ? DatePickerSX.enable : DatePickerSX.disable) } as any}
                // inputRef={ref}
                slots={{
                  openPickerIcon: CalendarMonthIcon,
                }}
                slotProps={{
                  textField: {
                    error: error?.message,
                    inputRef: ref,
                  },
                }}
              />
            )
          }}
        />
      </LocalizationProvider>
    </>
  )
}

export default DatePicker
